import {PopupRewardSuccess} from './PopupRewardSuccess'
import {recordPopupPage} from './popup-visit'
import type {SignupReward} from '@/modules/forms/types/form.types'
import { PopupReopenTab, readPopupReopenSettings } from './PopupReopenTab'
import { popupStore, popupKey, popupEnvironment, isPopupCheckout, popupAppearance, knownPopupSubscriber, rememberPopupSubscriber } from './popup-visibility'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { KibuPopup } from './KibuPopup'
import type { PopupSnapshot } from './KibuPopup'

import { getJourneyPopupEligibility, PopupEligibilityResponse, sendJourneyPopupEvent } from '@/services/api'
import useDiscountStore from '@/context/DiscountStore'
import { buildEventTrackingContext, readCookieConsentSnapshot } from '@/utils/attribution'
import { FormsApi } from '@/modules/forms/services/formsApi'
import { useRecaptcha } from '@/modules/forms/hooks/useRecaptcha'
import { getRegionFromUrl } from '@/utils/regionDetection'
import { popupHasFillHeight, PopupBlockRenderer, usePopupPreviewMode } from './PopupBlockRenderer'
import { PopupSurface, readPopupLayoutSettings, readCloseButtonSettings } from './PopupSurface'

const DEFAULT_DELAY_MS = 1200
const DEFAULT_DISMISS_COOLDOWN_MS = 24 * 60 * 60 * 1000

const buildDismissKey = (decision: PopupEligibilityResponse | null): string | null => {
    if (!decision?.flowNodeId && !decision?.popupVariantId) {
        return null
    }
    return `journey-popup-dismissed:${decision.flowNodeId || decision.popupVariantId}`
}

const normalizeEmail = (value: string): string => value.trim().toLowerCase()

const validateEmail = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

const readNumber = (value: unknown, fallback: number): number => {
    const n = Number(value)
    return Number.isFinite(n) ? n : fallback
}

const extractSnapshot = (decision: PopupEligibilityResponse | null): PopupSnapshot => {
    const raw = (decision?.popup?.snapshot || {}) as Record<string, unknown>
    const content = (raw.content || {}) as Record<string, unknown>
    const form = (raw.form || {}) as Record<string, unknown>
    const display = {
        ...((raw.display || {}) as Record<string, unknown>),
        ...((decision?.display || {}) as Record<string, unknown>),
    }
    const metadata = (raw.metadata || {}) as Record<string, unknown>

    return {
        content: {
            title: String(content.title || content.headline || raw.title || 'Join Kibu'),
            bodyText: String(content.bodyText || content.body || raw.bodyText || 'Sign up to hear from Kibu and unlock this offer.'),
            buttonText: String(content.buttonText || content.ctaLabel || form.submitLabel || raw.buttonText || 'Continue'),
            buttonSubtext: String(content.buttonSubtext || raw.buttonSubtext || ''),
            badgeText: (content.badgeText || raw.badgeText || display.badgeText || undefined) as string | undefined,
            heroImage: (content.heroImage || raw.heroImage || undefined) as string | undefined,
        },
        form: {
            formId: String(form.formId || metadata.formId || metadata.signupFormId || raw.formId || ''),
            successMessage: String(form.successMessage || raw.successMessage || 'Check your inbox for the next step.'),
        },
        display: {
            delayMs: readNumber(display.delayMs ?? raw.delayMs, DEFAULT_DELAY_MS),
            dismissCooldownMs: readNumber(display.dismissCooldownMs ?? raw.dismissCooldownMs, DEFAULT_DISMISS_COOLDOWN_MS),
        },
    }
}

const JourneyPopupRuntime: React.FC = () => {
    const location = useLocation()
    const popupPreviewMode = usePopupPreviewMode()
    const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''
    const { executeRecaptcha } = useRecaptcha({ siteKey: recaptchaSiteKey, enabled: true })
    const discountBlocked = useDiscountStore((s) => Boolean(s.discountCode?.valid || s.discountCode?.isLoading))

    const [claimedReward,setClaimedReward]=useState<SignupReward|null>(null)
    const eligibilityBlocked=discountBlocked&&!claimedReward
    const attemptRef=useRef({email:'',id:''})
    const visitorRef=useRef<'new'|'returning'|'unknown'>('unknown')
    const pageStartedRef=useRef(Date.now())
    const scrollRef=useRef(0)
    const idleUntilRef=useRef(0)
    const teaserTimerRef=useRef<number|null>(null)
    const [canReopen, setCanReopen] = useState(false)
    const pagesRef = useRef(0)
    const lastPathRef = useRef('')
    const visitStartRef = useRef(0)
    const foregroundShowRef = useRef<(() => void) | null>(null)
    const showReadyRef = useRef<(() => void) | null>(null)
    const [decision, setDecision] = useState<PopupEligibilityResponse | null>(null)
    const [isOpen, setIsOpen] = useState(false)
    const [email, setEmail] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isSuccess, setIsSuccess] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const showTimerRef = useRef<number | null>(null)
    const isMountedRef = useRef(true)
    const eligibilityEpochRef = useRef(0)
    // One accepted popup owns this page, including its delay and dismissed state.
    const pageDecisionRef = useRef<PopupEligibilityResponse | null>(null)

    const snapshot = useMemo(() => extractSnapshot(decision), [decision])

    const clearShowTimer = useCallback(() => {
        if(teaserTimerRef.current!==null){window.clearTimeout(teaserTimerRef.current);teaserTimerRef.current=null}
        if (showTimerRef.current !== null) {
            window.clearTimeout(showTimerRef.current)
            showTimerRef.current = null
        }
    }, [])

    const emitPopupEvent = async (
        eventName: 'popup_eligible' | 'popup_shown' | 'popup_dismissed' | 'popup_reopened' | 'popup_submitted',
        extra: Partial<Parameters<typeof sendJourneyPopupEvent>[0]> = {}
    ) => {
        const consent = readCookieConsentSnapshot()
        const trackingContext = buildEventTrackingContext(consent)
        await sendJourneyPopupEvent({
            ...trackingContext,
            eventName,
            experimentToken: decision?.experimentToken,
            decisionId: decision?.decisionId,
            flowId: decision?.flowId,
            flowNodeId: decision?.flowNodeId,
            popupVariantId: decision?.popupVariantId,
            offerId: decision?.offerId,
            ...extra,
        })
    }

    useEffect(() => {
        isMountedRef.current = true
        return () => {
            isMountedRef.current = false
            eligibilityEpochRef.current += 1
            clearShowTimer()
        }
    }, [clearShowTimer])

    const checkPopupEligibility = useCallback(async (
        triggerEvent: Parameters<typeof getJourneyPopupEligibility>[0]['triggerEvent'],
        countryCode?: string,
    ) => {
        if (eligibilityBlocked || isPopupCheckout(location.pathname) || pageDecisionRef.current) return
        const requestEpoch = eligibilityEpochRef.current

        try {
            const nextDecision = await getJourneyPopupEligibility({
                path: `${location.pathname}${location.search}`,
                triggerEvent,
                device: popupPreviewMode,
                visitorStatus:visitorRef.current,
                knownSubscriber:knownPopupSubscriber(),
                ...buildEventTrackingContext(readCookieConsentSnapshot()),
                countryCode: countryCode || getRegionFromUrl(location.pathname)?.toUpperCase(),
            })
            // Late product/page responses must not replace an accepted popup or a new route.
            if (!isMountedRef.current || requestEpoch !== eligibilityEpochRef.current || pageDecisionRef.current) return
            if (!nextDecision.eligible) return

            const display={...((nextDecision.popup?.snapshot as any)?.display || {}),...(nextDecision.display || {})}
            if ((display.suppressAfterSignup && popupStore.read(popupKey('signed-up',nextDecision))) || (display.suppressSubscribers && knownPopupSubscriber())) return
            const dismissKey = buildDismissKey(nextDecision)
            const storedDismissal=popupStore.read(popupKey('dismissed',nextDecision)) || (dismissKey?popupStore.read(dismissKey):null)
            const dismissedAt = storedDismissal ? Number(storedDismissal) : NaN
            const cooldownMs = readNumber(display.dismissCooldownMs, DEFAULT_DISMISS_COOLDOWN_MS)
            const inCooldown=Number.isFinite(dismissedAt) && Date.now()-dismissedAt<cooldownMs
            const seenAt=popupStore.read(popupKey('seen',nextDecision),true)
            const alreadySeen=seenAt==='1'||seenAt===String(visitStartRef.current)
            setCanReopen(inCooldown || alreadySeen)
            pageDecisionRef.current = nextDecision
            setDecision(nextDecision)
            void sendJourneyPopupEvent({
                ...buildEventTrackingContext(readCookieConsentSnapshot()),
                eventName: 'popup_eligible',
                decisionId: nextDecision.decisionId,
                flowId: nextDecision.flowId,
                flowNodeId: nextDecision.flowNodeId,
                popupVariantId: nextDecision.popupVariantId,
                offerId: nextDecision.offerId,
            }).catch(error=>console.warn('[Journeys] Eligibility event failed',error))

            const show=(trigger:'automatic'|'exit'='automatic')=>{
                clearShowTimer()
                if (!isMountedRef.current || requestEpoch !== eligibilityEpochRef.current || isPopupCheckout(location.pathname)) return
                if (document.hidden) { foregroundShowRef.current=()=>show(trigger); return }
                const a={...(display.appearance||{}),pageStartedAt:pageStartedRef.current}
                const status=popupAppearance({...display,appearance:a,currentScrollPercent:scrollRef.current},popupPreviewMode,pagesRef.current,visitStartRef.current,Date.now(),trigger)
                const focused=document.activeElement as HTMLElement|null
                const interacting=Date.now()<idleUntilRef.current||Boolean(focused?.matches('input,textarea,select,[contenteditable=true]'))||Boolean(document.querySelector('[role=dialog][aria-modal=true]:not(.journey-popup-surface)'))
                if(a.mode==='engaged'&&(!status.ready||interacting)) {
                    if(!a.exitOnly&&!a.manualOnly)showTimerRef.current=window.setTimeout(show,500)
                    return
                }
                foregroundShowRef.current=null
                showReadyRef.current=null
                setCanReopen(false)
                setIsOpen(true)
                popupStore.write(popupKey('seen',nextDecision),String(visitStartRef.current),true)
                void sendJourneyPopupEvent({...buildEventTrackingContext(readCookieConsentSnapshot()),eventName:'popup_shown',experimentToken:nextDecision.experimentToken,decisionId:nextDecision.decisionId,flowId:nextDecision.flowId,flowNodeId:nextDecision.flowNodeId,popupVariantId:nextDecision.popupVariantId,offerId:nextDecision.offerId}).catch(error=>console.warn('[Journeys] Popup impression failed',error))
            }
            const teaser=readPopupReopenSettings(nextDecision.popup?.snapshot||{},popupPreviewMode)
            if(teaser.enabled&&teaser.showBeforeDisplay)teaserTimerRef.current=window.setTimeout(()=>{if(!showReadyRef.current||!isOpen)setCanReopen(true)},Math.max(0,Number(teaser.beforeDisplayDelayMs??5000)))
            if(inCooldown || (display.oncePerSession && alreadySeen))return
            const appearance=popupAppearance(display,popupPreviewMode,pagesRef.current,visitStartRef.current,Date.now())
            if(appearance.exit && !alreadySeen)showReadyRef.current=()=>show('exit')
            if(appearance.delay!==null)showTimerRef.current=window.setTimeout(show,appearance.delay)

        } catch (error) {
            console.warn('[Journeys] Popup eligibility failed', error)
        }
    }, [eligibilityBlocked, location.pathname, location.search, popupPreviewMode, clearShowTimer])

    useEffect(() => {
        eligibilityEpochRef.current += 1
        clearShowTimer()
        foregroundShowRef.current=null
        showReadyRef.current=null
        setCanReopen(false)
        const sessionKey=`journey-popup:${popupEnvironment}:visit`
        const path=`${location.pathname}${location.search}`
        if(lastPathRef.current!==path){
            lastPathRef.current=path
            let visit:any={}
            try{visit=JSON.parse(popupStore.read(sessionKey,true)||'{}')}catch{}
            const tracked=recordPopupPage(location.pathname)
            pagesRef.current=tracked.pages
            visitStartRef.current=tracked.startedAt
            visitorRef.current=tracked.visitorStatus
            pageStartedRef.current=Date.now()
            scrollRef.current=0
            popupStore.write(sessionKey,JSON.stringify({pages:pagesRef.current,startedAt:visitStartRef.current}),true)
        }
        pageDecisionRef.current = null
        setDecision(null)
        setIsOpen(false)
        setIsSuccess(false)
        setClaimedReward(null)
        setErrorMessage(null)
        setEmail('')
        void checkPopupEligibility('page_view')
        return () => {
            eligibilityEpochRef.current += 1
            clearShowTimer()
        }
    }, [checkPopupEligibility, clearShowTimer])

    useEffect(()=>{
        const onScroll=(event:Event)=>{
            const target=event.target
            const element=target===document?document.documentElement:target as HTMLElement
            const top=element===document.documentElement?window.scrollY:element?.scrollTop||0
            const height=(element?.scrollHeight||0)-(element===document.documentElement?window.innerHeight:element?.clientHeight||0)
            if(height>0)scrollRef.current=Math.max(scrollRef.current,Math.min(100,100*top/height))
        }
        const interact=()=>{idleUntilRef.current=Date.now()+2000}
        document.addEventListener('scroll',onScroll,true)
        document.addEventListener('pointerdown',interact,true)
        document.addEventListener('keydown',interact,true)
        return()=>{document.removeEventListener('scroll',onScroll,true);document.removeEventListener('pointerdown',interact,true);document.removeEventListener('keydown',interact,true)}
    },[])

    useEffect(() => {
        const handleJourneyTrigger = (event: Event) => {
            const detail = (event as CustomEvent).detail || {}
            const triggerEvent = detail.triggerEvent
            if (triggerEvent !== 'product_view' && triggerEvent !== 'add_to_cart') return
            void checkPopupEligibility(triggerEvent, detail.countryCode)
        }

        window.addEventListener('kibu:journey-popup-trigger', handleJourneyTrigger)
        return () => {
            window.removeEventListener('kibu:journey-popup-trigger', handleJourneyTrigger)
        }
    }, [checkPopupEligibility])

    useEffect(()=>{
        if(popupPreviewMode!=='desktop')return
        const exit=(event:MouseEvent)=>{
            if(event.relatedTarget!==null || event.clientY>0)return
            if(showReadyRef.current)showReadyRef.current()
            else if(!pageDecisionRef.current)void checkPopupEligibility('exit_intent')
        }
        document.addEventListener('mouseout',exit)
        return ()=>document.removeEventListener('mouseout',exit)
    },[popupPreviewMode,checkPopupEligibility])

    useEffect(() => {
        const onVisible = () => { if (!document.hidden) foregroundShowRef.current?.() }
        document.addEventListener('visibilitychange', onVisible)
        return () => { document.removeEventListener('visibilitychange', onVisible); foregroundShowRef.current=null }
    }, [])

    const handleReopen=()=>{
        clearShowTimer()
        foregroundShowRef.current=null
        showReadyRef.current=null
        setCanReopen(false)
        setIsOpen(true)
        if(decision)popupStore.write(popupKey('seen',decision),String(visitStartRef.current),true)
        void emitPopupEvent('popup_shown').catch(error=>console.warn('[Journeys] Reopened impression failed',error))
        void emitPopupEvent('popup_reopened').catch(error=>console.warn('[Journeys] Reopening event failed',error))
    }

    const handleDismiss = async () => {
        eligibilityEpochRef.current += 1
        clearShowTimer()
        foregroundShowRef.current=null
        showReadyRef.current=null
        if (decision)popupStore.write(popupKey('dismissed',decision),String(Date.now()))
        setCanReopen(!isSuccess)
        setIsOpen(false)
        if (!isSuccess) await emitPopupEvent('popup_dismissed').catch(error=>console.warn('[Journeys] Dismissal event failed',error))
    }

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault()
        setErrorMessage(null)

        if (!validateEmail(email)) {
            setErrorMessage('Please enter a valid email address.')
            return
        }

        const formId = snapshot.form.formId
        if (!formId) {
            setErrorMessage('This popup is not fully configured yet.')
            return
        }

        setIsSubmitting(true)
        try {
            const recaptchaToken = await executeRecaptcha(`submit_${formId}`)
            if (!recaptchaToken) {
                setErrorMessage('Security check failed. Please try again.')
                return
            }

            const normalised=normalizeEmail(email)
            if(attemptRef.current.email!==normalised)attemptRef.current={email:normalised,id:Array.from(window.crypto.getRandomValues(new Uint8Array(24)),b=>b.toString(16).padStart(2,'0')).join('')}
            const submission = await FormsApi.submitForm(formId, {
                responses: { email: normalizeEmail(email) },
                metadata: {
                    sourcePage: window.location.href,
                    sourceTrigger: 'journey-popup',
                    signupAttemptId:attemptRef.current.id,
                    countryCode: (decision?.popup?.snapshot as any)?.product_value_country || getRegionFromUrl(location.pathname)?.toUpperCase(),
                    experimentToken: decision?.experimentToken,
                    flowId: decision?.flowId,
                    flowNodeId: decision?.flowNodeId,
                    popupVariantId: decision?.popupVariantId,
                    offerId: decision?.offerId,
                },
                recaptchaToken,
            })

            if(submission?.signupReward){
                const reward=submission.signupReward
                setClaimedReward(reward)
                useDiscountStore.getState().setDiscountCode(reward.code)
                useDiscountStore.getState().setDiscountValidation({...reward,signupEmail:normalised,valid:true,error:null})
            }
            if(decision)popupStore.write(popupKey('signed-up',decision),'1')
            rememberPopupSubscriber(normalised)
            setCanReopen(false)
            void emitPopupEvent('popup_submitted', { email: normalizeEmail(email) }).catch(error=>console.warn('[Journeys] Signup event failed',error))
            setEmail(normalizeEmail(email))
            setIsSuccess(true)
            if(decision)popupStore.write(popupKey('dismissed',decision),String(Date.now()))
        } catch (error) {
            console.error('Journey popup submission failed:', error)
            setErrorMessage(error instanceof Error ? error.message : 'Something went wrong. Please try again.')
        } finally {
            setIsSubmitting(false)
        }
    }

    if (!decision || eligibilityBlocked || isPopupCheckout(location.pathname)) return null

    const rawSnapshot = (decision.popup?.snapshot || {}) as Record<string, any>
    const blockTemplate = rawSnapshot.template as { content_blocks: any[]; design_tokens?: any } | undefined
    const reopenSettings=readPopupReopenSettings(rawSnapshot,popupPreviewMode)
    const reopenTab=!isOpen && canReopen && !isSuccess ? <PopupReopenTab settings={reopenSettings} onReopen={handleReopen} /> : null
    if(claimedReward&&isSuccess){
        const layout=readPopupLayoutSettings(rawSnapshot,popupPreviewMode)
        const heading=String(rawSnapshot.form?.successMessage||'').trim()
        return <PopupSurface accessibleLabel={heading||claimedReward.label||'Your offer is ready'} variant={layout.variant} maxWidth={layout.maxWidth} backgroundColor={blockTemplate?.design_tokens?.backgroundColor||'#ffffff'} isOpen={isOpen} onDismiss={()=>void handleDismiss()}>
            <PopupRewardSuccess code={claimedReward.code} label={claimedReward.label} description={claimedReward.description} heading={heading} emailBound={claimedReward.emailBound} tokens={blockTemplate?.design_tokens} onContinue={()=>void handleDismiss()}/>
        </PopupSurface>
    }
    const hasBlocks = Array.isArray(blockTemplate?.content_blocks) && blockTemplate!.content_blocks.length > 0

    if (hasBlocks) {
        const closeButton = readCloseButtonSettings(rawSnapshot, popupPreviewMode)
        const popupLayout = readPopupLayoutSettings(rawSnapshot, popupPreviewMode)
        const formProps = {
            email,
            isSubmitting,
            isSuccess,
            errorMessage,
            onEmailChange: setEmail,
            onSubmit: (e: React.FormEvent) => { void handleSubmit(e) },
        }
        return (
            <> {reopenTab}<PopupSurface accessibleLabel={snapshot.content.title} reopenTarget={reopenSettings.enabled && !isSuccess ? '.journey-popup-reopen' : undefined} backgroundColor={blockTemplate!.design_tokens?.backgroundColor || '#ffffff'} cornerRadius={blockTemplate!.design_tokens?.blockRadius ?? 24} fillContentHeight={popupHasFillHeight(blockTemplate!, popupPreviewMode)} variant={popupLayout.variant} maxWidth={popupLayout.maxWidth} contentVerticalAlign={popupLayout.contentVerticalAlign} closeButton={closeButton} isOpen={isOpen} onDismiss={() => { void handleDismiss() }}>
                <PopupBlockRenderer template={blockTemplate!} formProps={formProps} fillHeight={popupLayout.variant === 'full_screen'} />
            </PopupSurface></>
        )
    }

    return (
        <>{reopenTab}<KibuPopup
            snapshot={snapshot}
            reopenTarget={reopenSettings.enabled && !isSuccess ? '.journey-popup-reopen' : undefined}
            isOpen={isOpen}
            isSuccess={isSuccess}
            isSubmitting={isSubmitting}
            email={email}
            errorMessage={errorMessage}
            onEmailChange={setEmail}
            onSubmit={(e) => { void handleSubmit(e) }}
            onDismiss={() => { void handleDismiss() }}
        /></>
    )

}

export default JourneyPopupRuntime
